import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserRoleE } from '@enums/user-role.enum';
import { environment } from '@environments/environment';
import { PageI } from '@interfaces/page.interface';
import { UserPreferencesI } from '@interfaces/user-preferences.interface';
import { UserFilterI, UserI } from '@interfaces/user.interface';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private slug_url = 'users/';
  constructor(private http: HttpClient) {}

  public createUser(user: UserI): Observable<UserI> {
    return this.http.post<UserI>(environment.OMNISMART_API_USERS + this.slug_url, user);
  }

  public validateRamal(ramal: string, userId?: string): Observable<{ available: boolean }> {
    let params = new HttpParams().set('ramal', ramal);
    if (userId) {
      params = params.set('userId', userId);
    }
    return this.http.get<{ available: boolean }>(
      `${environment.OMNISMART_API_USERS}${this.slug_url}ramal/validate`,
      { params }
    );
  }

  public getUsersByCustomerId(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt',
    systemProfileId: string = '',
    customer = '',
    filter: UserFilterI = {}
  ): Observable<PageI<UserI>> {
    let params = new HttpParams()
      .set('search', search)
      .set('systemProfile', systemProfileId)
      .set('sort', sort)
      .set('active', active)
      .set('page', page.toString())
      .set('customer', customer)
      .set('skipOwnUser', true)
      .set('levelFilter', true);

    if (filter) {
      params = params.set('filter', JSON.stringify(filter));
    }
    if (limit) {
      params = params.set('limit', limit.toString());
    }

    return this.http.get<PageI<UserI>>(environment.OMNISMART_API_USERS + this.slug_url, {
      params,
    });
  }

  public getAllUsersByCustomerId(params?: {
    search?: string;
    skipOwnUser?: boolean;
    levelFilter?: boolean;
    teams?: string | string[];
    excludeBlocked?: boolean;
  }): Observable<PageI<UserI>> {
    return this.http.get<PageI<UserI>>(environment.OMNISMART_API_USERS + this.slug_url, {
      params: {
        page: 1,
        limit: 99999,
        ...params,
      },
    });
  }

  public getAllUsersByCurrentUser() {
    return this.http.get<UserI[]>(`${environment.OMNISMART_API_USERS + this.slug_url}all-by-current-user`).pipe(
      map((result) => {
        return result.sort((first, second) => (first.name > second.name ? 1 : second.name < first.name ? -1 : 0));
      })
    );
  }

  public getAgentsByCurrentUserProfileLevel() {
    return this.http
      .get<UserI[]>(`${environment.OMNISMART_API_USERS + this.slug_url}agents-by-current-user-profile-level`)
      .pipe(
        map((result) => {
          return result.sort((first, second) => (first.name > second.name ? 1 : second.name < first.name ? -1 : 0));
        })
      );
  }

  public getUserById(userId: string | undefined): Observable<UserI> {
    return this.http.get<UserI>(environment.OMNISMART_API_USERS + this.slug_url + userId);
  }

  public getUserEmailById(userId: string | undefined): Observable<any> {
    return {} as any;
  }

  public updateUser(
    id: string,
    user: Partial<UserI>,
    options: { force: boolean } = { force: false }
  ): Observable<UserI> {
    return this.http.patch<UserI>(environment.OMNISMART_API_USERS + this.slug_url + id, user, {
      params: {
        force: options?.force ? 'true' : 'false',
      },
    });
  }

  public updateUserPreferences(id: string, userPreferences: UserPreferencesI): Observable<void> {
    return this.http.patch<void>(
      environment.OMNISMART_API_USERS + this.slug_url + 'preferences/' + id,
      userPreferences
    );
  }

  public deleteUser(id: string, options: { force: boolean } = { force: false }): Observable<any> {
    return this.http.delete(environment.OMNISMART_API_USERS + this.slug_url + id, {
      params: {
        force: options?.force ? 'true' : 'false',
      },
    });
  }

  public updateRoles(rows: Array<any>): Observable<any> {
    const url = `${environment.OMNISMART_API_USERS}${this.slug_url}update-roles`;
    return this.http.post(url, rows);
  }

  public getCountUsers(): Observable<number> {
    return this.http.get<number>(environment.OMNISMART_API_USERS + this.slug_url + 'count');
  }

  public getUsersByRole(role: UserRoleE, params?: { search?: string; length?: number }): Observable<UserI[]> {
    return this.http.get<UserI[]>(environment.OMNISMART_API_USERS + this.slug_url + `role/${role}`, {
      params,
    });
  }

  public logoffUser(userId: string) {
    return this.http.post(`${environment.OMNISMART_API_USERS}${this.slug_url}logoff/${userId}`, {});
  }

  public unlockUser(userId: string) {
    return this.http.patch(`${environment.OMNISMART_API_USERS}${this.slug_url}unblock/${userId}`, {});
  }

  public exportUsersCSV(search: string, systemProfile: string, filter: any, customer: string = ''): Observable<any> {
    const params = this.buildExportParams(search, systemProfile, filter, customer);
    return this.http.get<any>(`${environment.OMNISMART_API_USERS}${this.slug_url}csv/exports`, {
      responseType: 'blob' as 'json',
      params,
    });
  }

  public exportUsersExcel(search: string, systemProfile: string, filter: any, customer: string = ''): Observable<any> {
    const params = this.buildExportParams(search, systemProfile, filter, customer);
    return this.http.get<any>(`${environment.OMNISMART_API_USERS}${this.slug_url}excel/exports`, {
      responseType: 'blob' as 'json',
      params,
    });
  }

  public exportUsersTxt(search: string, systemProfile: string, filter: any, customer: string = ''): Observable<any> {
    const params = this.buildExportParams(search, systemProfile, filter, customer);
    return this.http.get<any>(`${environment.OMNISMART_API_USERS}${this.slug_url}txt/exports`, {
      responseType: 'blob' as 'json',
      params,
    });
  }

  public importUploadUsers(file: File): Observable<{ token: string; counters: { total: number; ok: number; failed: number } }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ token: string; counters: { total: number; ok: number; failed: number } }>(
      `${environment.OMNISMART_API_USERS}${this.slug_url}import/upload`,
      formData
    );
  }

  public importCommitUsers(token: string): Observable<{ token: string; total: number }> {
    return this.http.post<{ token: string; total: number }>(
      `${environment.OMNISMART_API_USERS}${this.slug_url}import/commit/${token}`,
      {}
    );
  }

  public getUsersImportStatus(): Observable<{
    total: number;
    imported: number;
    skipped: number;
    errors: { line: number; reason: string }[];
    status: 'running' | 'completed';
  } | null> {
    return this.http.get<any>(`${environment.OMNISMART_API_USERS}${this.slug_url}import/status`);
  }

  public downloadUsersImportTemplate(format: 'xlsx' | 'csv'): Observable<Blob> {
    return this.http.get(`${environment.OMNISMART_API_USERS}${this.slug_url}import/template`, {
      responseType: 'blob',
      params: new HttpParams().set('format', format),
    });
  }

  private buildExportParams(search: string, systemProfile: string, filter: any, customer: string): HttpParams {
    let params = new HttpParams()
      .set('search', search || '')
      .set('systemProfile', systemProfile || '')
      .set('customer', customer || '')
      .set('skipOwnUser', 'true')
      .set('levelFilter', 'true');
    if (filter) {
      params = params.set('filter', JSON.stringify(filter));
    }
    return params;
  }
}
